/**
 * FAQ Tabbed Custom Element
 * A web component for FAQ tabbed interface with expand/collapse functionality
 */
class FAQTabbedElement extends HTMLElement {
  constructor() {
    super();
    
    // Bind methods to maintain proper context
    this.handleTabSwitch = this.handleTabSwitch.bind(this);
    this.handleFAQToggle = this.handleFAQToggle.bind(this);
    this.handleViewMoreToggle = this.handleViewMoreToggle.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    this.handleFAQKeydown = this.handleFAQKeydown.bind(this);
  }

  static get observedAttributes() {
    return ['items-to-show', 'active-tab'];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'active-tab' && this.isConnected && oldValue !== newValue) {
      this.switchToTab(newValue);
    }
    if (name === 'items-to-show' && this.isConnected && oldValue !== newValue) {
      this.updateViewMoreButtons();
    }
  }

  /**
   * Initialize all functionality
   */
  init() {
    try {
      // Get items to show from attribute or default
      this.itemsToShow = parseInt(this.getAttribute('items-to-show')) || 6;
      
      // Cache DOM elements
      this.cacheElements();
      
      this.bindTabSwitching();
      this.bindFAQToggle();
      this.bindViewMoreToggle();
      this.ensureActiveTab();
    } catch (error) {
      console.error('Error initializing FAQ section:', error);
    }
  }

  cacheElements() {
    // Cache DOM elements with fallback to empty arrays to prevent forEach errors
    this.tabButtons = this.querySelectorAll('.faq-tab-btn') || [];
    this.tabContents = this.querySelectorAll('.faq-tab-content') || [];
    this.faqQuestions = this.querySelectorAll('.faq-question') || [];
    this.viewMoreButtons = this.querySelectorAll('.faq-view-more-btn') || [];
    
    // Ensure we have NodeLists or arrays that can be iterated
    if (!this.tabButtons.length) this.tabButtons = [];
    if (!this.tabContents.length) this.tabContents = [];
    if (!this.faqQuestions.length) this.faqQuestions = [];
    if (!this.viewMoreButtons.length) this.viewMoreButtons = [];
  }

  /**
   * Handle tab switching functionality
   */
  bindTabSwitching() {
    if (!this.tabButtons || !this.tabButtons.length) return;
    
    this.tabButtons.forEach((button) => {
      if (!button) return;
      
      // Remove existing listeners to prevent duplicates
      button.removeEventListener('click', this.handleTabSwitch);
      button.removeEventListener('keydown', this.handleTabKeydown);
      
      // Add event listeners
      button.addEventListener('click', this.handleTabSwitch);
      button.addEventListener('keydown', this.handleTabKeydown);
    });
  }

  handleTabKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleTabSwitch(e);
    }
  }

  /**
   * Handle FAQ expand/collapse functionality
   */
  bindFAQToggle() {
    if (!this.faqQuestions || !this.faqQuestions.length) return;
    
    this.faqQuestions.forEach((question) => {
      if (!question) return;
      
      // Remove existing listeners to prevent duplicates
      question.removeEventListener('click', this.handleFAQToggle);
      question.removeEventListener('keydown', this.handleFAQKeydown);
      
      // Add event listeners
      question.addEventListener('click', this.handleFAQToggle);
      question.addEventListener('keydown', this.handleFAQKeydown);
    });
  }

  handleFAQKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleFAQToggle(e);
    }
  }

  /**
   * Handle view more/less functionality
   */
  bindViewMoreToggle() {
    if (!this.viewMoreButtons || !this.viewMoreButtons.length) return;
    
    this.viewMoreButtons.forEach((button) => {
      if (!button) return;
      
      // Remove existing listeners to prevent duplicates
      button.removeEventListener('click', this.handleViewMoreToggle);
      
      // Add event listeners
      button.addEventListener('click', this.handleViewMoreToggle);
    });
  }

  /**
   * Handle tab switching
   */
  handleTabSwitch(event) {
    try {
      if (!event || !event.currentTarget) return;
      
      const targetTab = event.currentTarget.getAttribute('data-tab');
      if (!targetTab) return;
      
      // Remove active class from all tabs and content
      if (this.tabButtons && this.tabButtons.length) {
        this.tabButtons.forEach(btn => {
          if (btn) {
            btn.classList.remove('active');
            btn.setAttribute('aria-selected', 'false');
          }
        });
      }
      
      if (this.tabContents && this.tabContents.length) {
        this.tabContents.forEach(content => {
          if (content) content.classList.remove('active');
        });
      }
      
      // Add active class to clicked tab and corresponding content
      event.currentTarget.classList.add('active');
      event.currentTarget.setAttribute('aria-selected', 'true');
      const targetContent = this.querySelector(`.faq-tab-content[data-tab="${targetTab}"]`);
      if (targetContent) {
        targetContent.classList.add('active');
      }

      // Update attribute to reflect active tab (only if different)
      if (this.getAttribute('active-tab') !== targetTab) {
        this.setAttribute('active-tab', targetTab);
      }
      
      // Dispatch custom event for external listeners
      this.dispatchEvent(new CustomEvent('faq:tab-switch', {
        detail: { activeTab: targetTab },
        bubbles: true
      }));
    } catch (error) {
      console.error('Error switching tabs:', error);
    }
  }

  /**
   * Handle FAQ item expand/collapse
   */
  handleFAQToggle(event) {
    try {
      if (!event || !event.currentTarget) return;
      
      const faqItem = event.currentTarget.closest('.faq-item');
      if (!faqItem) return;
      
      const answer = faqItem.querySelector('.faq-answer');
      if (!answer) return;
      
      const isExpanded = faqItem.classList.contains('expanded');
      const faqQuestion = faqItem.querySelector('.faq-question');
      
      if (isExpanded) {
        // Collapse
        faqItem.classList.remove('expanded');
        answer.style.maxHeight = '0';
        answer.setAttribute('aria-hidden', 'true');
        faqQuestion && faqQuestion.setAttribute('aria-expanded', 'false');
      } else {
        // Expand
        faqItem.classList.add('expanded');
        faqQuestion && faqQuestion.setAttribute('aria-expanded', 'true');
        answer.style.maxHeight = answer.scrollHeight + 'px';
        answer.setAttribute('aria-hidden', 'false');
      }

      // Dispatch custom event for external listeners
      this.dispatchEvent(new CustomEvent('faq:item-toggle', {
        detail: { 
          faqItem,
          expanded: !isExpanded 
        },
        bubbles: true
      }));
    } catch (error) {
      console.error('Error toggling FAQ:', error);
    }
  }

  /**
   * Handle view more/less toggle
   */
  handleViewMoreToggle(event) {
    try {
      if (!event || !event.currentTarget) return;
      
      const tabId = event.currentTarget.getAttribute('data-tab');
      if (!tabId) return;
      
      const tabContent = this.querySelector(`.faq-tab-content[data-tab="${tabId}"]`);
      if (!tabContent) return;
      
      const hiddenItems = tabContent.querySelectorAll('.faq-item.faq-hidden');
      const allItems = tabContent.querySelectorAll('.faq-item');
      const viewMoreText = event.currentTarget.querySelector('.view-more-text');
      const viewLessText = event.currentTarget.querySelector('.view-less-text');
      
      if (!viewMoreText || !viewLessText) return;
      
      // Check if we're currently showing more items
      const isShowingMore = viewMoreText.style.display === 'none';
      
      if (isShowingMore) {
        // Hide items beyond the initial count (View Less)
        if (allItems && allItems.length) {
          allItems.forEach((item, index) => {
            if (item && index >= this.itemsToShow) {
              item.classList.add('faq-hidden');
            }
          });
        }
        viewMoreText.style.display = 'inline';
        viewLessText.style.display = 'none';
      } else {
        // Show all hidden items (View More)
        if (hiddenItems && hiddenItems.length) {
          hiddenItems.forEach(item => {
            if (item) {
              item.classList.remove('faq-hidden');
            }
          });
        }
        viewMoreText.style.display = 'none';
        viewLessText.style.display = 'inline';
      }

      // Dispatch custom event for external listeners
      this.dispatchEvent(new CustomEvent('faq:view-more-toggle', {
        detail: { 
          tabId,
          showingMore: !isShowingMore 
        },
        bubbles: true
      }));
    } catch (error) {
      console.error('Error toggling view more:', error);
    }
  }

  /**
   * Ensure first tab is active if no active tab exists
   */
  ensureActiveTab() {
    if (!this.tabButtons || !this.tabButtons.length) return;
    
    if (!this.querySelector('.faq-tab-btn.active') && this.tabButtons.length > 0) {
      this.tabButtons[0].classList.add('active');
      if (this.tabContents && this.tabContents.length > 0) {
        this.tabContents[0].classList.add('active');
        
        // Set initial active tab attribute
        const firstTabId = this.tabButtons[0].getAttribute('data-tab');
        if (firstTabId) {
          this.setAttribute('active-tab', firstTabId);
        }
      }
    }
  }

  /**
   * Update view more buttons based on current settings
   */
  updateViewMoreButtons() {
    if (!this.tabContents || !this.tabContents.length) return;
    
    this.tabContents.forEach(tabContent => {
      if (!tabContent) return;
      
      const allItems = tabContent.querySelectorAll('.faq-item');
      if (!allItems || !allItems.length) return;
      
      allItems.forEach((item, index) => {
        if (!item) return;
        
        if (index >= this.itemsToShow) {
          item.classList.add('faq-hidden');
        } else {
          item.classList.remove('faq-hidden');
        }
      });
    });
  }

  // Public API methods
  /**
   * Programmatically switch to a specific tab
   */
  switchToTab(tabId) {
    if (!tabId) return;
    
    try {
      // Remove active class from all tabs and content
      if (this.tabButtons && this.tabButtons.length) {
        this.tabButtons.forEach(btn => {
          if (btn) btn.classList.remove('active');
        });
      }
      
      if (this.tabContents && this.tabContents.length) {
        this.tabContents.forEach(content => {
          if (content) content.classList.remove('active');
        });
      }
      
      // Add active class to target tab and corresponding content
      const targetButton = this.querySelector(`.faq-tab-btn[data-tab="${tabId}"]`);
      const targetContent = this.querySelector(`.faq-tab-content[data-tab="${tabId}"]`);
      
      if (targetButton) {
        targetButton.classList.add('active');
      }
      
      if (targetContent) {
        targetContent.classList.add('active');
      }
      
      // Dispatch custom event for external listeners
      this.dispatchEvent(new CustomEvent('faq:tab-switch', {
        detail: { activeTab: tabId },
        bubbles: true
      }));
    } catch (error) {
      console.error('Error switching tabs:', error);
    }
  }

  /**
   * Programmatically expand a specific FAQ item
   */
  expandFAQ(faqItemId) {
    const faqItem = this.querySelector(`[data-item="${faqItemId}"]`);
    if (faqItem && !faqItem.classList.contains('expanded')) {
      const question = faqItem.querySelector('.faq-question');
      if (question) {
        question.click();
      }
    }
  }

  /**
   * Get currently active tab
   */
  get activeTab() {
    return this.getAttribute('active-tab');
  }

  /**
   * Set items to show per tab
   */
  set itemsToShow(value) {
    this.setAttribute('items-to-show', value.toString());
  }

  /**
   * Get items to show per tab
   */
  get itemsToShow() {
    return parseInt(this.getAttribute('items-to-show')) || 6;
  }

  /**
   * Cleanup method for removing event listeners
   */
  cleanup() {
    if (this.tabButtons && this.tabButtons.length) {
      this.tabButtons.forEach(button => {
        if (button) {
          button.removeEventListener('click', this.handleTabSwitch);
          button.removeEventListener('keydown', this.handleTabKeydown);
        }
      });
    }
    
    if (this.faqQuestions && this.faqQuestions.length) {
      this.faqQuestions.forEach(question => {
        if (question) {
          question.removeEventListener('click', this.handleFAQToggle);
          question.removeEventListener('keydown', this.handleFAQKeydown);
        }
      });
    }
    
    if (this.viewMoreButtons && this.viewMoreButtons.length) {
      this.viewMoreButtons.forEach(button => {
        if (button) {
          button.removeEventListener('click', this.handleViewMoreToggle);
        }
      });
    }
  }
}

// Register the custom element
if (!customElements.get('faq-tabbed')) {
  customElements.define('faq-tabbed', FAQTabbedElement);
}

// Legacy support - convert existing elements to custom elements
function upgradeExistingFAQElements() {
  try {
    // Only select elements that are not already custom elements and have the right structure
    const legacyElements = document.querySelectorAll('[id^="faq-"]:not(faq-tabbed)');
    
    legacyElements.forEach(element => {
      // Skip if already processed or is already a custom element
      if (element.tagName.toLowerCase() === 'faq-tabbed' || element.hasAttribute('data-faq-upgraded')) {
        return;
      }
      
      // Check if element has FAQ structure before upgrading
      const hasFAQStructure = element.querySelector('.faq-tab-btn') || element.querySelector('.faq-item');
      if (!hasFAQStructure) {
        return;
      }
      
      // Mark as processed to prevent multiple upgrades
      element.setAttribute('data-faq-upgraded', 'true');
      
      // Create new custom element
      const customElement = document.createElement('faq-tabbed');
      
      // Copy attributes (excluding the marker attribute)
      Array.from(element.attributes).forEach(attr => {
        if (attr.name !== 'data-faq-upgraded') {
          customElement.setAttribute(attr.name, attr.value);
        }
      });
      
      // Copy dataset attributes to custom element attributes
      if (element.dataset.itemsToShow) {
        customElement.setAttribute('items-to-show', element.dataset.itemsToShow);
      }
      
      // Move content
      customElement.innerHTML = element.innerHTML;
      
      // Replace in DOM only if parent exists
      if (element.parentNode) {
        element.parentNode.replaceChild(customElement, element);
      }
    });
  } catch (error) {
    console.error('Error upgrading FAQ elements:', error);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', upgradeExistingFAQElements);
} else {
  upgradeExistingFAQElements();
}

// Handle Shopify design mode events
const isDesignMode = window.Shopify && (window.Shopify.designMode === true || window.location.search.includes('preview_theme_id'));

if (isDesignMode) {
  // Section load event
  document.addEventListener('shopify:section:load', function(event) {
    setTimeout(() => {
      try {
        if (!event.target) return;
        
        const legacyElements = event.target.querySelectorAll('[id^="faq-"]:not(faq-tabbed)');
        legacyElements.forEach(element => {
          // Skip if already processed or is already a custom element
          if (element.tagName.toLowerCase() === 'faq-tabbed' || element.hasAttribute('data-faq-upgraded')) {
            return;
          }
          
          // Check if element has FAQ structure before upgrading
          const hasFAQStructure = element.querySelector('.faq-tab-btn') || element.querySelector('.faq-item');
          if (!hasFAQStructure) {
            return;
          }
          
          // Mark as processed
          element.setAttribute('data-faq-upgraded', 'true');
          
          const customElement = document.createElement('faq-tabbed');
          Array.from(element.attributes).forEach(attr => {
            if (attr.name !== 'data-faq-upgraded') {
              customElement.setAttribute(attr.name, attr.value);
            }
          });
          if (element.dataset.itemsToShow) {
            customElement.setAttribute('items-to-show', element.dataset.itemsToShow);
          }
          customElement.innerHTML = element.innerHTML;
          
          if (element.parentNode) {
            element.parentNode.replaceChild(customElement, element);
          }
        });
      } catch (error) {
        console.error('Error in section load handler:', error);
      }
    }, 0);
  });

  // Block select event for theme editor
  document.addEventListener('shopify:block:select', function(event) {
    try {
      if (!event || !event.target) return;
      
      const blockElement = event.target;
      const faqElement = blockElement.closest('faq-tabbed');
      
      if (faqElement && typeof faqElement.switchToTab === 'function') {
        // Find which tab this block belongs to and switch to it
        const faqItem = blockElement.closest('.faq-item');
        if (faqItem) {
          const tabContent = faqItem.closest('.faq-tab-content');
          if (tabContent) {
            const tabId = tabContent.getAttribute('data-tab');
            if (tabId) {
              faqElement.switchToTab(tabId);
            }
          }
          
          // Expand the selected FAQ item
          const itemId = faqItem.getAttribute('data-item');
          if (itemId && typeof faqElement.expandFAQ === 'function') {
            faqElement.expandFAQ(itemId);
          }
        }
      }
    } catch (error) {
      console.error('Error in block select handler:', error);
    }
  });
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FAQTabbedElement;
}
